import { useState, useRef } from 'react'
import {
  Box,
  VStack,
  HStack,
  Stack,
  Text,
  Button,
  NativeSelect,
  Badge,
  Spinner,
  Tabs,
  Dialog,
  Portal,
  useDisclosure,
  type TabsValueChangeDetails,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiPlus, FiSearch, FiTrash2, FiEdit2, FiBriefcase, FiUpload, FiGitMerge, FiUserPlus } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useWorksiteSearch, useDeleteWorksite, useBusinesses, useBusinessSearch, useDeleteBusiness } from '../hooks/useBusiness'
import { BulkImportModal } from '../components/business/BulkImportModal'
import { BusinessFormModal } from '../components/business/BusinessFormModal'
import { BusinessChainFormModal } from '../components/business/BusinessChainManager'
import { MergeBusinessModal, MergeWorksiteModal } from '../components/business/MergeBusinessModal'
import { AddMemberWizard } from '../components/business/AddMemberWizard'
import { ResponsiveTable, FilterBar, type ResponsiveColumn } from '../components/shared'
import type { WorksiteSearchParams, BusinessSearchParams, WorksiteSummary, BusinessSummary } from '../types/business'

export function BusinessListPage() {
  const navigate = useNavigate()
  const cancelRef = useRef<HTMLButtonElement>(null!)

  // Worksite tab state
  const [wsParams, setWsParams] = useState<WorksiteSearchParams>({ page: 1, pageSize: 25 })
  const [wsSearchInput, setWsSearchInput] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string; type: 'worksite' | 'business' } | null>(null)

  // Tab state
  const [tabIndex, setTabIndex] = useState(0)

  // Business tab state
  const [bizParams, setBizParams] = useState<BusinessSearchParams>({ page: 1, pageSize: 25 })
  const [bizSearchInput, setBizSearchInput] = useState('')

  const { data: wsData, isLoading: wsLoading, error: wsError } = useWorksiteSearch(wsParams)
  const { data: businesses } = useBusinesses()
  const { data: bizData, isLoading: bizLoading, error: bizError } = useBusinessSearch(bizParams)
  const deleteWsMutation = useDeleteWorksite()
  const deleteBizMutation = useDeleteBusiness()
  const importModal = useDisclosure()
  const createModal = useDisclosure()
  const createBizModal = useDisclosure()
  const mergeBizModal = useDisclosure()
  const mergeWsModal = useDisclosure()
  const addMemberModal = useDisclosure()

  const handleWsSearch = () => {
    setWsParams(prev => ({ ...prev, search: wsSearchInput || undefined, page: 1 }))
  }

  const handleBizSearch = () => {
    setBizParams(prev => ({ ...prev, search: bizSearchInput || undefined, page: 1 }))
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      if (deleteTarget.type === 'worksite') {
        await deleteWsMutation.mutateAsync(deleteTarget.id)
        toaster.create({ title: 'Worksite deleted', type: 'success', duration: 3000 })
      } else {
        await deleteBizMutation.mutateAsync(deleteTarget.id)
        toaster.create({ title: 'Business deleted', type: 'success', duration: 3000 })
      }
      setDeleteTarget(null)
    } catch {
      toaster.create({ title: `Failed to delete ${deleteTarget.type}`, type: 'error', duration: 3000 })
    }
  }

  const wsTotalPages = wsData ? Math.ceil(wsData.totalCount / wsData.pageSize) : 0
  const bizTotalPages = bizData ? Math.ceil(bizData.totalCount / bizData.pageSize) : 0

  const wsFilterCount = (wsParams.type ? 1 : 0) + (wsParams.businessId ? 1 : 0)
  const bizFilterCount = bizParams.type ? 1 : 0

  const worksiteColumns: ResponsiveColumn<WorksiteSummary>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (ws) => <Text fontWeight="medium">{ws.label}</Text>,
    },
    {
      header: 'Type',
      mobileRole: 'badge',
      cell: (ws) => ws.type ? <Badge bg="info.subtle" color="info.fg" fontSize="xs">{ws.type}</Badge> : null,
    },
    {
      header: 'Location',
      mobileRole: 'meta',
      cell: (ws) => (
        <Text fontSize="sm" color="gray.600">
          {[ws.city, ws.state].filter(Boolean).join(', ') || '---'}
        </Text>
      ),
    },
    {
      header: 'Business',
      mobileRole: 'meta',
      cell: (ws) => <Text fontSize="sm" color="gray.600">{ws.businessName || '---'}</Text>,
    },
    {
      header: 'Employees',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (ws) => <Text fontSize="sm">{ws.employeeCount}</Text>,
    },
    {
      header: 'Phone',
      mobileRole: 'desktop-only',
      cell: (ws) => <Text fontSize="sm" color="gray.600">{ws.phone || '---'}</Text>,
    },
  ]

  const businessColumns: ResponsiveColumn<BusinessSummary>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (biz) => <Text fontWeight="medium">{biz.label}</Text>,
    },
    {
      header: 'Type',
      mobileRole: 'badge',
      cell: (biz) => biz.type ? <Badge bg="info.subtle" color="info.fg" fontSize="xs">{biz.type}</Badge> : null,
    },
    {
      header: 'Locations',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (biz) => <Text fontSize="sm">{biz.worksiteCount}</Text>,
    },
  ]

  return (
    <Box p={8}>
      <VStack gap={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" flexWrap="wrap" gap={3}>
          <Box>
            <Text fontSize="2xl" fontWeight="bold">Worksites & Businesses</Text>
            <Text color="gray.500" fontSize="sm">
              Manage worksite locations, businesses, and employer data
            </Text>
          </Box>
          <Stack direction={{ base: 'column', md: 'row' }} gap={2} w={{ base: 'full', md: 'auto' }}>
            <Button
              variant="outline"
              colorPalette="green"
              onClick={addMemberModal.onOpen}
            ><FiUserPlus />
              Add Member
            </Button>
            <Button
              variant="outline"
              colorPalette="brand"
              onClick={importModal.onOpen}
            ><FiUpload />
              Import CSV
            </Button>
            <Button
              colorPalette="brand"
              onClick={tabIndex === 0 ? createModal.onOpen : createBizModal.onOpen}
            ><FiPlus />
              {tabIndex === 0 ? 'Add Worksite' : 'Add Business'}
            </Button>
          </Stack>
        </HStack>

        <Tabs.Root
          colorPalette="brand"
          variant="enclosed"
          value={tabIndex === 0 ? 'worksites' : 'businesses'}
          onValueChange={(details: TabsValueChangeDetails) => setTabIndex(details.value === 'worksites' ? 0 : 1)}
        >
          <Tabs.List>
            <Tabs.Trigger value="worksites">Worksites</Tabs.Trigger>
            <Tabs.Trigger value="businesses">Businesses</Tabs.Trigger>
          </Tabs.List>
          <Tabs.ContentGroup>
            {/* === Worksites Tab === */}
            <Tabs.Content value="worksites" px={0}>
              <VStack gap={6} align="stretch">
                <FilterBar
                  search={{
                    value: wsSearchInput,
                    onChange: setWsSearchInput,
                    placeholder: 'Search worksites...',
                    onSubmit: handleWsSearch,
                  }}
                  filterCount={wsFilterCount}
                  actions={
                    <>
                      <Button size="sm" colorPalette="brand" onClick={handleWsSearch}><FiSearch />Search</Button>
                      {wsData && wsData.items.length >= 2 && (
                        <Button
                          size="sm"
                          variant="outline"
                          colorPalette="orange"
                          onClick={mergeWsModal.onOpen}
                        ><FiGitMerge />
                          Merge
                        </Button>
                      )}
                    </>
                  }
                >
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      maxW={{ base: 'full', md: '160px' }}
                      placeholder="All types"
                      value={wsParams.type ?? ''}
                      onChange={e => setWsParams(prev => ({ ...prev, type: e.target.value || undefined, page: 1 }))}
                    >
                      <option value="Restaurant">Restaurant</option>
                      <option value="Retail">Retail</option>
                      <option value="Office">Office</option>
                      <option value="Factory">Factory</option>
                      <option value="Warehouse">Warehouse</option>
                      <option value="Service">Service</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Education">Education</option>
                      <option value="Government">Government</option>
                      <option value="Other">Other</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      maxW={{ base: 'full', md: '180px' }}
                      placeholder="All businesses"
                      value={wsParams.businessId ?? ''}
                      onChange={e => setWsParams(prev => ({ ...prev, businessId: e.target.value || undefined, page: 1 }))}
                    >
                      {businesses?.map(b => (
                        <option key={b.id} value={b.id}>{b.label}</option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </FilterBar>

                {wsLoading && (
                  <VStack py={12}>
                    <Spinner size="lg" color="accent" />
                    <Text color="gray.500">Loading worksites...</Text>
                  </VStack>
                )}

                {!!wsError && (
                  <Box bg="red.50" p={4} borderRadius="md" color="red.600">
                    Failed to load worksites. Please try again.
                  </Box>
                )}

                {!wsLoading && wsData && wsData.items.length === 0 && (
                  <Box bg="surface" p={12} borderRadius="lg" shadow="md" textAlign="center">
                    <Box as={FiBriefcase} mx="auto" boxSize={12} color="gray.300" mb={4} />
                    <Text color="gray.500" mb={4}>
                      No worksites found. {wsParams.search ? 'Try a different search.' : 'Add your first worksite.'}
                    </Text>
                    {!wsParams.search && (
                      <Button colorPalette="brand" onClick={createModal.onOpen}>
                        <FiPlus />Add Worksite
                      </Button>
                    )}
                  </Box>
                )}

                {!wsLoading && wsData && wsData.items.length > 0 && (
                  <ResponsiveTable
                    columns={worksiteColumns}
                    rows={wsData.items}
                    keyFn={(ws) => ws.id}
                    onRowClick={(ws) => navigate(`/dashboard/worksites/${ws.id}`)}
                    rowActions={[
                      { label: 'Edit', icon: FiEdit2, onClick: (ws) => navigate(`/dashboard/worksites/${ws.id}`) },
                      { label: 'Delete', icon: FiTrash2, color: 'red.500', onClick: (ws) => setDeleteTarget({ id: ws.id, label: ws.label, type: 'worksite' }) },
                    ]}
                  />
                )}

                {!wsLoading && wsData && wsTotalPages > 1 && (
                  <HStack justify="space-between" px={4} py={3}>
                    <Text fontSize="sm" color="gray.500">
                      Showing {((wsData.page - 1) * wsData.pageSize) + 1}--{Math.min(wsData.page * wsData.pageSize, wsData.totalCount)} of {wsData.totalCount}
                    </Text>
                    <HStack gap={2}>
                      <Button size="sm" variant="outline" disabled={wsData.page <= 1}
                        onClick={() => setWsParams(prev => ({ ...prev, page: (prev.page ?? 1) - 1 }))}
                      >Previous</Button>
                      <Text fontSize="sm" color="gray.600">Page {wsData.page} of {wsTotalPages}</Text>
                      <Button size="sm" variant="outline" disabled={wsData.page >= wsTotalPages}
                        onClick={() => setWsParams(prev => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
                      >Next</Button>
                    </HStack>
                  </HStack>
                )}
              </VStack>
            </Tabs.Content>

            {/* === Businesses Tab === */}
            <Tabs.Content value="businesses" px={0}>
              <VStack gap={6} align="stretch">
                <FilterBar
                  search={{
                    value: bizSearchInput,
                    onChange: setBizSearchInput,
                    placeholder: 'Search businesses...',
                    onSubmit: handleBizSearch,
                  }}
                  filterCount={bizFilterCount}
                  actions={
                    <>
                      <Button size="sm" colorPalette="brand" onClick={handleBizSearch}><FiSearch />Search</Button>
                      {bizData && bizData.items.length >= 2 && (
                        <Button
                          size="sm"
                          variant="outline"
                          colorPalette="orange"
                          onClick={mergeBizModal.onOpen}
                        ><FiGitMerge />
                          Merge
                        </Button>
                      )}
                    </>
                  }
                >
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      maxW={{ base: 'full', md: '160px' }}
                      placeholder="All types"
                      value={bizParams.type ?? ''}
                      onChange={e => setBizParams(prev => ({ ...prev, type: e.target.value || undefined, page: 1 }))}
                    >
                      <option value="Restaurant">Restaurant</option>
                      <option value="Retail">Retail</option>
                      <option value="Office">Office</option>
                      <option value="Factory">Factory</option>
                      <option value="Warehouse">Warehouse</option>
                      <option value="Service">Service</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Education">Education</option>
                      <option value="Government">Government</option>
                      <option value="Other">Other</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </FilterBar>

                {bizLoading && (
                  <VStack py={12}>
                    <Spinner size="lg" color="accent" />
                    <Text color="gray.500">Loading businesses...</Text>
                  </VStack>
                )}

                {!!bizError && (
                  <Box bg="red.50" p={4} borderRadius="md" color="red.600">
                    Failed to load businesses. Please try again.
                  </Box>
                )}

                {!bizLoading && bizData && bizData.items.length === 0 && (
                  <Box bg="surface" p={12} borderRadius="lg" shadow="md" textAlign="center">
                    <Box as={FiBriefcase} mx="auto" boxSize={12} color="gray.300" mb={4} />
                    <Text color="gray.500">
                      No businesses found. {bizParams.search ? 'Try a different search.' : ''}
                    </Text>
                  </Box>
                )}

                {!bizLoading && bizData && bizData.items.length > 0 && (
                  <ResponsiveTable
                    columns={businessColumns}
                    rows={bizData.items}
                    keyFn={(biz) => biz.id}
                    onRowClick={(biz) => navigate(`/dashboard/businesses/${biz.id}`)}
                    rowActions={[
                      { label: 'Delete', icon: FiTrash2, color: 'red.500', onClick: (biz) => setDeleteTarget({ id: biz.id, label: biz.label, type: 'business' }) },
                    ]}
                  />
                )}

                {!bizLoading && bizData && bizTotalPages > 1 && (
                  <HStack justify="space-between" px={4} py={3}>
                    <Text fontSize="sm" color="gray.500">
                      Showing {((bizData.page - 1) * bizData.pageSize) + 1}--{Math.min(bizData.page * bizData.pageSize, bizData.totalCount)} of {bizData.totalCount}
                    </Text>
                    <HStack gap={2}>
                      <Button size="sm" variant="outline" disabled={bizData.page <= 1}
                        onClick={() => setBizParams(prev => ({ ...prev, page: (prev.page ?? 1) - 1 }))}
                      >Previous</Button>
                      <Text fontSize="sm" color="gray.600">Page {bizData.page} of {bizTotalPages}</Text>
                      <Button size="sm" variant="outline" disabled={bizData.page >= bizTotalPages}
                        onClick={() => setBizParams(prev => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
                      >Next</Button>
                    </HStack>
                  </HStack>
                )}
              </VStack>
            </Tabs.Content>
          </Tabs.ContentGroup>
        </Tabs.Root>
      </VStack>

      {/* Delete confirmation */}
      <Dialog.Root
        open={!!deleteTarget}
        initialFocusEl={() => cancelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            setDeleteTarget(null);
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Delete {deleteTarget?.type === 'business' ? 'Business' : 'Worksite'}
                </Dialog.Header>
                <Dialog.Body>
                  Are you sure you want to delete <strong>{deleteTarget?.label}</strong>? This will deactivate the record.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={() => setDeleteTarget(null)}>Cancel</Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDelete}
                    ml={3}
                    loading={deleteWsMutation.isPending || deleteBizMutation.isPending}
                  >
                    Delete
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      {/* Import modal */}
      <BulkImportModal isOpen={importModal.open} onClose={importModal.onClose} />

      {/* Create worksite modal */}
      {createModal.open && (
        <BusinessFormModal
          isOpen={createModal.open}
          onClose={createModal.onClose}
          onCreated={(id) => navigate(`/dashboard/worksites/${id}`)}
        />
      )}

      {/* Create business modal */}
      {createBizModal.open && (
        <BusinessChainFormModal
          isOpen={createBizModal.open}
          onClose={createBizModal.onClose}
          business={null}
        />
      )}

      {/* Merge business modal */}
      {mergeBizModal.open && bizData && bizData.items.length >= 2 && (
        <MergeBusinessModal
          isOpen={mergeBizModal.open}
          onClose={mergeBizModal.onClose}
          items={bizData.items}
        />
      )}

      {/* Merge worksite modal */}
      {mergeWsModal.open && wsData && wsData.items.length >= 2 && (
        <MergeWorksiteModal
          isOpen={mergeWsModal.open}
          onClose={mergeWsModal.onClose}
          items={wsData.items}
        />
      )}

      {/* Add Member wizard */}
      {addMemberModal.open && (
        <AddMemberWizard
          isOpen={addMemberModal.open}
          onClose={addMemberModal.onClose}
        />
      )}
    </Box>
  );
}
